import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import PropTypes from "prop-types"
import { forwardRef } from "react"

import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { cn } from "styles/utils"

const variants = ["checkout", "primary", "secondary", "tertiary"]
const sizes = {
  lg: "h-48 px-24",
  md: "h-44 px-24",
  sm: "h-36 px-16",
}

// Apply `size` styles only when `variant` is set and not a `text` button
const sizeCompoundVariants = variants.flatMap((variant) =>
  Object.entries(sizes).map(([size, className]) => ({
    class: className,
    size,
    text: false, // size styles only when text is false
    variant,
  }))
)

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center disabled:cursor-not-allowed aria-disabled:cursor-not-allowed",
  {
    compoundVariants: [
      {
        // primary button
        class:
          "bg-black-print text-white hover:bg-[linear-gradient(rgba(255,255,255,0.2)_0_0)] active:bg-[linear-gradient(rgba(255,255,255,0.4)_0_0)]",
        text: false,
        variant: "primary",
      },
      {
        // primary link/text
        class: "text-black active:brightness-200",
        text: true,
        variant: "primary",
      },
      {
        // secondary button
        class:
          "bg-primary text-white hover:bg-[linear-gradient(rgba(0,0,0,0.2)_0_0)] active:bg-[linear-gradient(rgba(0,0,0,0.4)_0_0)]",
        text: false,
        variant: "secondary",
      },
      {
        // secondary link/text
        class: "text-primary active:brightness-60",
        text: true,
        variant: "secondary",
      },
      ...sizeCompoundVariants,
    ],
    defaultVariants: {
      size: "lg",
      text: false,
      underline: false,
    },
    variants: {
      size: {
        lg: null,
        sm: null,
      },
      text: {
        false: null,
        true: "h-auto px-0",
      },
      underline: {
        false: null,
        true: "underline hover:no-underline focus:no-underline",
      },
      variant: {
        checkout:
          "text-16 aria-disabled:bg-grey-300 aria-disabled:text-grey-500 disabled:bg-grey-300 disabled:text-grey-500 bg-emerald whitespace-nowrap font-semibold leading-none text-white hover:bg-[linear-gradient(rgba(0,0,0,0.2)_0_0)] active:bg-[linear-gradient(rgba(0,0,0,0.4)_0_0)] active:opacity-100 disabled:hover:bg-none disabled:active:bg-none aria-disabled:hover:bg-none aria-disabled:active:bg-none",
        primary:
          "text-16 aria-disabled:bg-grey-300 aria-disabled:text-grey-500 disabled:bg-grey-300 disabled:text-grey-500 whitespace-nowrap font-semibold leading-none active:opacity-100 disabled:hover:bg-none disabled:active:bg-none aria-disabled:hover:bg-none aria-disabled:active:bg-none",
        secondary:
          "text-16 aria-disabled:bg-grey-300 aria-disabled:text-grey-500 disabled:bg-grey-300 disabled:text-grey-500 whitespace-nowrap font-semibold leading-none active:opacity-100 disabled:hover:bg-none disabled:active:bg-none aria-disabled:hover:bg-none aria-disabled:active:bg-none",
        tertiary:
          "text-16 aria-disabled:bg-grey-300 aria-disabled:text-grey-500 disabled:bg-grey-300 disabled:text-grey-500 whitespace-nowrap bg-white font-semibold leading-none text-black hover:bg-[linear-gradient(rgba(0,0,0,0.05)_0_0)] active:bg-[linear-gradient(rgba(0,0,0,0.10)_0_0)] active:opacity-100 disabled:hover:bg-none disabled:active:bg-none aria-disabled:hover:bg-none aria-disabled:active:bg-none",
      },
    },
  }
)

const Button = forwardRef(
  (
    {
      children,
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      underline = false,
      text = false,
      ...props
    },
    ref
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(
            buttonVariants({
              className,
              size,
              text,
              underline,
              variant,
            })
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      )
    }

    return (
      <button
        className={cn(
          buttonVariants({
            className,
            size,
            text,
            underline,
            variant,
          })
        )}
        ref={ref}
        type="button"
        {...props}
      >
        {children}
        {loading && <FontAwesomeIcon className="ml-4" icon={faSpinner} spin />}
      </button>
    )
  }
)

Button.displayName = "Button"

Button.propTypes = {
  asChild: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  loading: PropTypes.bool,
  size: PropTypes.oneOf(["lg", "md", "sm"]),
  text: PropTypes.bool,
  underline: PropTypes.bool,
  variant: PropTypes.oneOf(["checkout", "primary", "secondary", "tertiary"]),
}

export default Button
